/**
 * /academy/course/method — « Méthode et astuces » (D-107): what to do, and how, for each section
 * and each type of question of the exam. Always open (free when the agency offers it), it never
 * blocks the path. The same page serves the TCF and the IELTS.
 */

import { useAcademyCourseMethodRetrieve } from "@agencyos/api-client";
import { type PathUnit, parseMethod } from "@agencyos/core";
import { Badge, EmptyState, GlassCard, Skeleton, buttonClass } from "@agencyos/ui-web";
import { ArrowLeft, Lightbulb, Lock } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { PageHead } from "../ui";
import { ContentLangProvider } from "./contentLang";
import { UnitCard } from "./CoursePage";
import { useCourseExam, useCourseText } from "./courseText";
import { courseExam } from "./exam";
import { InMyLanguage } from "./InMyLanguage";

/** The method block of an exam (units and lessons, all open). */
export function useMethod(exam: string, enabled = true) {
  const params = { exam: courseExam(exam) } as const;
  const query = useAcademyCourseMethodRetrieve(params, { query: { retry: false, enabled, staleTime: 30_000 } });
  const method = useMemo(() => (query.data?.status === 200 ? parseMethod(query.data.data) : null), [query.data]);
  return { query, method };
}

export function MethodPage() {
  const exam = useCourseExam();
  return <MethodView key={exam} exam={exam} />;
}

function MethodView({ exam }: { exam: string }) {
  const { t, i18n } = useTranslation();
  const { contentLang } = useCourseText(exam);
  const { query, method } = useMethod(exam);
  const back = (
    <Link to="/academy/course" className="ac-back">
      <ArrowLeft aria-hidden size={16} />
      {t("academyCourse.method.toPath")}
    </Link>
  );
  if (query.isLoading) return <Skeleton style={{ height: 320 }} />;
  if (!method) {
    return (
      <div className="ac-page">
        {back}
        <GlassCard className="ac-card ac-card--center">
          <p className="ko">{t("common.error")}</p>
        </GlassCard>
      </div>
    );
  }
  const units: PathUnit[] = method.units.map((u) => ({
    id: u.id,
    code: u.code,
    title: u.title,
    skills: [],
    lessons: u.lessons.map((l) => ({ id: l.id, code: l.code, title: l.title, state: l.state === "completed" ? "completed" : "unlocked", score: null, exercises: 0 })),
  }));
  return (
    <ContentLangProvider lang={contentLang}>
      <div className="ac-page ac-course">
        {back}
        <PageHead eyebrow={t(`ielts.exams.${exam}.name`)} title={t("academyCourse.method.title")} subtitle={<p>{t("academyCourse.method.intro")}</p>} />
        <InMyLanguage text={t("academyCourse.method.intro")} source={i18n.language.startsWith("en") ? "en" : "fr"} kind="method" />
        {method.free && method.open && <Badge tone="success">{t("academyCourse.method.free")}</Badge>}
        {!method.open ? (
          <GlassCard className="ac-card">
            <EmptyState
              icon={<Lock aria-hidden size={22} />}
              title={t("academyCourse.method.lockedTitle")}
              description={t("academyCourse.method.lockedText")}
              actions={
                <Link className={buttonClass("primary")} to="/academy/plans">
                  {t("academy.plans.choose")}
                </Link>
              }
            />
          </GlassCard>
        ) : units.length === 0 ? (
          <GlassCard className="ac-card">
            <EmptyState icon={<Lightbulb aria-hidden size={22} />} title={t("academyCourse.method.empty")} description={t("academyCourse.method.emptyText")} />
          </GlassCard>
        ) : (
          <div className="ac-units">
            {units.map((unit, index) => (
              <UnitCard key={unit.id} unit={unit} next={null} index={index} exam={exam} />
            ))}
          </div>
        )}
      </div>
    </ContentLangProvider>
  );
}
